import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { useDispatch } from 'react-redux'
import { MobileCancelMajorMonotone } from '@shopify/polaris-icons'
import axios from 'axios'
import {
  Sheet,
  Heading,
  Button,
  FormLayout,
  Modal,
  TextContainer,
  Spinner
} from '@shopify/polaris'
import {
  actionDeletePin,
  actionDuplicatePin,
  actionSetActivePin,
  actionSetToastMessage,
  actionToggleToastActive,
  actionUpdatePinField
} from '../store/actions'
import { extractIdFromGraphQL } from '../libs/utils'

import AccordionItem from './accordion-item/accordion-item'
import FileInputComponent from './file-input'
import SelectComponent from './select-input'
import ResourcePickerInputComponent from './resource-picker-input'
import TextInputComponent from './text-input'
import ColorPickerComponent from './color-picker-input'
import CheckboxComponent from './checkbox-input'
import MapperPreviewComponent from './mapper-preview/mapper-preview'

const MapperPinsOptionsComponent = (props) => {
  const dispatch = useDispatch()
  const activePinIndex = useSelector(state => state.activePin)
  const activeItem = useSelector(state => state.activeItem)
  const pin = activeItem.pins[activePinIndex]
  const [loading, setLoading] = useState(false)
  const [missingDataSource, setMissingDataSource] = useState(false)

  // Modal
  const [activeDeleteModal, setActiveDeleteModal] = useState(false)
  const toggleDeleteModalChange = useCallback(() => setActiveDeleteModal(!activeDeleteModal), [activeDeleteModal])
  const [isDeletingPin, setIsDeletingPin] = useState(false)

  /**
   * show alert if data source is empty
   */
  const checkPinDataSource = () => {
    let result = false
    if (pin.isPreset || pin.display_data === 'html') {
      result = true
    }
    if (
      pin.display_data === 'product'
      && pin.product_id
      && pin.product_id.length
    ) {
      result = true
    }
    if (
      pin.display_data === 'collection'
      && pin.collection_id
      && pin.collection_id.length
    ) {
      result = true
    }
    if (!result) {
      dispatch(actionSetToastMessage('Data source field is required.'))
      dispatch(actionToggleToastActive(true))
      setMissingDataSource(true)
      setLoading(false)
    }
    return result
  }

  // Slideout
  const optionsAreaRef = useRef()
  const sheetActive = (activePinIndex !== null)
  const closeSheet = useCallback(() => {
    if (checkPinDataSource()) {
      dispatch(actionSetActivePin(null))
    }
  }, [])
  const isPreset = (pin && pin.hasOwnProperty('isPreset')) ? pin.isPreset : false

  const resourcePickerLabel = pin && pin.display_data === 'product' ?
    'Browse product' :
    'Browse collection'

  /**
   * Handle picker results from data source
   * @param type
   * @param selection
   */
  const handleResourcePicker = (type, selection) => {
    if (selection) {
      const masterId = extractIdFromGraphQL(selection.id)
      const handle = selection.handle
      dispatch(actionUpdatePinField('master_id', masterId))
      setMissingDataSource(false)
      switch (type) {
        case 'product':
          dispatch(actionUpdatePinField('product_id', handle))
          if (
            selection.variants.length > 0 &&
            selection.variants[0].title !== 'Default Title'
          ) {
            const selectedVariants = selection.variants.map(variant => extractIdFromGraphQL(variant.id))
            dispatch(actionUpdatePinField('product_variants', selectedVariants.join(',')))
          }
          break;
        case 'collection':
          dispatch(actionUpdatePinField('collection_id', handle))
          break;
      }
    }
  }

  /**
   * Dispatch an event to redux to update pins settings
   * @param key
   * @param value
   */
  const updatePinSetting = (key, value) => {
    if (key === 'preset_id') {
      value = parseInt(value)
    }
    dispatch(actionUpdatePinField(key, value, activePinIndex))
  }

  // auto set preset to first default preset
  useEffect(() => {
    const defaultPresetId = props.presetList && props.presetList.length ? props.presetList[0].value : 0
    if (pin.preset_id === null) dispatch(actionUpdatePinField('preset_id', defaultPresetId, activePinIndex))
    if (activeItem && activeItem.dev) dispatch(actionUpdatePinField('preset_id', 0, activePinIndex))
  }, [])

  // auto scroll to top on error
  useEffect(() => {
    if (missingDataSource) {
      optionsAreaRef.current.scrollTop = 1
    }
  }, [missingDataSource])

  const title = isPreset
    ? pin.hasOwnProperty('preset_name')
      ? `preset ${pin.preset_name}`
      : `preset #${activePinIndex + 1 || 0}`
    : `pin #${activePinIndex + 1 || 0}`

  /**
   * Delete pin by ID
   */
  const deletePin = () => {
    if (pin.id) {
      setIsDeletingPin(true)
      axios.delete(`/api/v2/admin/pin/delete`, {
        data: { id: pin.id }
      })
        .then(() => {
          setIsDeletingPin(false)
          dispatch(actionDeletePin())
          dispatch(actionSetActivePin(null))
        })
        .catch((e) => {
          console.log(e)
        })
    } else {
      dispatch(actionDeletePin())
      dispatch(actionSetActivePin(null))
    }
  }

  /**
   * Duplicate pin
   */
  const duplicatePin = () => {
    if (checkPinDataSource()) {
      const pinWidth = 65
      const wrapperWidth = props.imageDimension.width
      const existingLeftPosition = parseFloat(pin.left) * wrapperWidth / 100
      const newLeftPosition = (existingLeftPosition + pinWidth) / wrapperWidth * 100
      const duplicatePin = { ...pin, left: `${newLeftPosition}%` }
      // remove id to create new pin
      delete duplicatePin.id
      dispatch(actionDuplicatePin(duplicatePin))
      dispatch(actionSetActivePin(null))
    }
  }

  /**
   * Save pin settings
   * @returns {Promise<void>}
   */
  const savePinAction = async () => {
    setLoading(true)
    if (checkPinDataSource()) {
      if (props.onSaveAction && props.onSaveIsPromise) {
        props.onSaveAction()
          .then((res) => {
            setLoading(false)
            if (props.onSavedAction) props.onSavedAction()
          })
          .catch((e) => {
            console.log(e)
          })
      } else {
        props.onSaveAction()
        setLoading(false)
      }
    }
  }

  return (
    <div>
      <Modal
        open={activeDeleteModal}
        onClose={toggleDeleteModalChange}
        title={`Delete ${title}?`}
        primaryAction={{
          content: `${isDeletingPin ? 'Deleting' : 'Delete'} ${isPreset ? 'preset' : 'pin'}`,
          destructive: true,
          disabled: isDeletingPin,
          onAction: deletePin,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            disabled: isDeletingPin,
            onAction: toggleDeleteModalChange,
          }
        ]}
      >
        <Modal.Section>
          {isDeletingPin && <Spinner accessibilityLabel="Deleting pin" size="large" color="teal" />}
          {!isDeletingPin && <TextContainer>
            <p>
              Are you sure you want to delete the {title}? This can’t be undone.
            </p>
          </TextContainer>}
        </Modal.Section>
      </Modal>
      {!activeDeleteModal &&
      <Sheet open={sheetActive} onClose={closeSheet}>
        <div
          className={loading ? 'loading-wrapper' : null}
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
          }}
        >
          {loading && <Spinner accessibilityLabel="Saving data" size="large" color="teal" />}
          <div
            style={{
              alignItems: 'center',
              borderBottom: '1px solid #DFE3E8',
              display: 'flex',
              justifyContent: 'space-between',
              padding: '1.6rem',
              width: '100%'
            }}
          >
            <Heading>
              <span style={{
                textTransform: 'capitalize'
              }}>{title}</span>
            </Heading>
            <Button
              accessibilityLabel='Cancel'
              icon={MobileCancelMajorMonotone}
              onClick={closeSheet}
              plain
            />
          </div>
          <div
            ref={optionsAreaRef}
            style={{
              height: '100%',
              overflow: 'auto'
            }}>
            <div>
              <div
                className="accordion-item__content"
                style={{
                  padding: '1.6rem',
                  borderBottom: '1px solid #DFE3E8'
                }}
              >
                {isPreset && <TextInputComponent
                  label='Preset name'
                  name='preset_name'
                  value={pin.preset_name}
                  onChange={updatePinSetting}
                />}
                {
                  !isPreset &&
                  props.presetList &&
                  props.presetList.length &&
                  pin.preset_id !== null &&
                  pin.preset_id !== false &&
                  <SelectComponent
                    label='Pin preset'
                    options={props.presetList}
                    name='preset_id'
                    value={pin.preset_id}
                    onChange={updatePinSetting}
                  />
                }
              </div>
              {!isPreset && <AccordionItem
                label='Data source'
                forceActive={missingDataSource}
              >
                <FormLayout>
                  <SelectComponent
                    label='Data source'
                    options={[
                      { label: 'Product', value: 'product' },
                      { label: 'Collection', value: 'collection' },
                      { label: 'HTML', value: 'html' },
                    ]}
                    name='display_data'
                    value={pin.display_data}
                    onChange={updatePinSetting}
                  />
                  {
                    pin.display_data === 'product' &&
                    <ResourcePickerInputComponent
                      value={pin.product_id}
                      dataType={pin.display_data}
                      buttonLabel={resourcePickerLabel}
                      label={resourcePickerLabel}
                      onChange={handleResourcePicker}
                      error={missingDataSource ? "Product is required" : false}
                    />
                  }
                  {
                    pin.display_data === 'collection' &&
                    <ResourcePickerInputComponent
                      value={pin.collection_id}
                      dataType={pin.display_data}
                      buttonLabel={resourcePickerLabel}
                      label={resourcePickerLabel}
                      onChange={handleResourcePicker}
                      error={missingDataSource ? "Collection is required" : false}
                    />
                  }
                  {
                    pin.display_data === 'html' &&
                    <TextInputComponent
                      name='product_title'
                      value={pin.product_title}
                      onChange={updatePinSetting}
                      label='Title'
                    />
                  }
                  {
                    pin.display_data === 'html' &&
                    <TextInputComponent
                      name='product_description'
                      multiline={4}
                      value={pin.product_description}
                      onChange={updatePinSetting}
                      label='Description'
                    />
                  }
                </FormLayout>
              </AccordionItem>}

              {pin.display_data !== 'html' &&
              <AccordionItem
                label='Translation'
              >
                <FormLayout>
                  {!isPreset && <TextInputComponent
                    label='Override quickview title'
                    name='product_title'
                    value={pin.product_title}
                    onChange={updatePinSetting}
                  />}
                  {!isPreset && <TextInputComponent
                    label='Override quickview description'
                    name='product_description'
                    value={pin.product_description}
                    onChange={updatePinSetting}
                  />}
                  {
                    (
                      isPreset ||
                      (!isPreset && parseInt(pin.preset_id) === 0 && pin.display_data !== 'html')
                    ) &&
                    <TextInputComponent
                    label='Quickview view item text'
                    name='product_btn_one'
                    value={pin.product_btn_one}
                    onChange={updatePinSetting}
                  />}
                  {
                    (
                      isPreset ||
                      (!isPreset && parseInt(pin.preset_id) === 0 && pin.display_data !== 'html')
                    ) &&
                    <TextInputComponent
                    label='Quickview add to cart text'
                    name='product_btn_two'
                    value={pin.product_btn_two}
                    onChange={updatePinSetting}
                  />}
                  {
                    (
                      isPreset ||
                      (!isPreset && parseInt(pin.preset_id) === 0 && pin.display_data !== 'html')
                    ) &&
                    <TextInputComponent
                    label='Quickview product unavailable text'
                    name='product_unavailable'
                    value={pin.product_unavailable}
                    onChange={updatePinSetting}
                  />}
                  {
                    (
                      isPreset ||
                      (!isPreset && parseInt(pin.preset_id) === 0 && pin.display_data !== 'html')
                    ) &&
                    <TextInputComponent
                    label='Quickview product added to cart text'
                    name='added_to_cart_text'
                    value={pin.added_to_cart_text}
                    onChange={updatePinSetting}
                  />}
                </FormLayout>
              </AccordionItem>}

              {parseInt(pin.preset_id) === 0 && <AccordionItem
                label='Pin'
              >
                <FormLayout>
                  <FileInputComponent
                    label='Icon'
                    helpText='Recommended size: 30x30px'
                    name='icon_url'
                    value={pin.icon_url}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Background'
                    name='pin_background'
                    value={pin.pin_background}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Hover background'
                    name='pin_hover_background'
                    value={pin.pin_hover_background}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Border color'
                    name='pin_border'
                    value={pin.pin_border}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Hover border color'
                    name='pin_hover_border'
                    value={pin.pin_hover_border}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Dot color'
                    name='pin_dot'
                    value={pin.pin_dot}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Hover dot color'
                    name='pin_hover_dot'
                    value={pin.pin_hover_dot}
                    onChange={updatePinSetting}
                  />
                </FormLayout>
              </AccordionItem>}

              {parseInt(pin.preset_id) === 0 && !activeItem.disable_tooltip && <AccordionItem
                label='Hover Pop-up'>
                <FormLayout>
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Background'
                    name='popup_background'
                    value={pin.popup_background}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Shadow background'
                    name='popup_shadow_color'
                    value={pin.popup_shadow_color}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Text text color'
                    name='popup_title_color'
                    value={pin.popup_title_color}
                    onChange={updatePinSetting}
                  />
                  <TextInputComponent
                    name='popup_title_size'
                    value={pin.popup_title_size}
                    onChange={updatePinSetting}
                    label='Font size'
                  />
                  <SelectComponent
                    label='Font style'
                    options={[
                      { label: 'Normal', value: 'normal' },
                      { label: 'Italic', value: 'italic' },
                      { label: 'Oblique', value: 'oblique' },
                    ]}
                    name='popup_title_font_style'
                    value={pin.popup_title_font_style}
                    onChange={updatePinSetting}
                  />
                  <SelectComponent
                    label='Font weight'
                    options={[
                      { label: '100', value: '100' },
                      { label: '200', value: '200' },
                      { label: '300', value: '300' },
                      { label: '400', value: '400' },
                      { label: '500', value: '500' },
                      { label: '600', value: '600' },
                      { label: '700', value: '700' },
                      { label: '800', value: '800' },
                      { label: '900', value: '900' },
                    ]}
                    name='popup_title_font_weight'
                    value={pin.popup_title_font_weight}
                    onChange={updatePinSetting}
                  />
                </FormLayout>
              </AccordionItem>}

              {parseInt(pin.preset_id) === 0 && <AccordionItem
                label='Quickview'>
                <FormLayout>
                  <CheckboxComponent
                    label='Show image(s)'
                    name='quickview_enable_image'
                    checked={pin.quickview_enable_image}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Background'
                    name='quickview_background'
                    value={pin.quickview_background}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Line color'
                    name='quickview_line_color'
                    value={pin.quickview_line_color}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Shadow color'
                    name='quickview_shadow_color'
                    value={pin.quickview_shadow_color}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Close icon color'
                    name='quickview_close_color'
                    value={pin.quickview_close_color}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Close icon hover color'
                    name='quickview_close_hover_color'
                    value={pin.quickview_close_hover_color}
                    onChange={updatePinSetting}
                  />
                </FormLayout>
              </AccordionItem>}

              {parseInt(pin.preset_id) === 0 && <AccordionItem
                label='Quickview title'>
                <FormLayout>
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Text color'
                    name='product_title_color'
                    value={pin.product_title_color}
                    onChange={updatePinSetting}
                  />
                  <TextInputComponent
                    label='Font size'
                    name='product_title_size'
                    value={pin.product_title_size}
                    onChange={updatePinSetting}
                  />
                  <SelectComponent
                    label='Font style'
                    options={[
                      { label: 'Normal', value: 'normal' },
                      { label: 'Italic', value: 'italic' },
                      { label: 'Oblique', value: 'oblique' },
                    ]}
                    name='product_title_font_style'
                    value={pin.product_title_font_style}
                    onChange={updatePinSetting}
                  />
                  <SelectComponent
                    label='Font weight'
                    options={[
                      { label: '100', value: '100' },
                      { label: '200', value: '200' },
                      { label: '300', value: '300' },
                      { label: '400', value: '400' },
                      { label: '500', value: '500' },
                      { label: '600', value: '600' },
                      { label: '700', value: '700' },
                      { label: '800', value: '800' },
                      { label: '900', value: '900' },
                    ]}
                    name='product_title_font_weight'
                    value={pin.product_title_font_weight}
                    onChange={updatePinSetting}
                  />
                </FormLayout>
              </AccordionItem>}

              {parseInt(pin.preset_id) === 0 && <AccordionItem
                label='Quickview description'>
                <FormLayout>
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Text color'
                    name='product_description_color'
                    value={pin.product_description_color}
                    onChange={updatePinSetting}
                  />
                  <TextInputComponent
                    label='Font size'
                    name='product_description_size'
                    value={pin.product_description_size}
                    onChange={updatePinSetting}
                  />
                  <SelectComponent
                    label='Font style'
                    options={[
                      { label: 'Normal', value: 'normal' },
                      { label: 'Italic', value: 'italic' },
                      { label: 'Oblique', value: 'oblique' },
                    ]}
                    name='product_description_font_style'
                    value={pin.product_description_font_style}
                    onChange={updatePinSetting}
                  />
                  <SelectComponent
                    label='Font weight'
                    options={[
                      { label: '100', value: '100' },
                      { label: '200', value: '200' },
                      { label: '300', value: '300' },
                      { label: '400', value: '400' },
                      { label: '500', value: '500' },
                      { label: '600', value: '600' },
                      { label: '700', value: '700' },
                      { label: '800', value: '800' },
                      { label: '900', value: '900' },
                    ]}
                    name='product_description_font_weight'
                    value={pin.product_description_font_weight}
                    onChange={updatePinSetting}
                  />
                </FormLayout>
              </AccordionItem>}

              {parseInt(pin.preset_id) === 0 && <AccordionItem
                label='Quickview price'>
                <FormLayout>
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Text color'
                    name='product_price_color'
                    value={pin.product_price_color}
                    onChange={updatePinSetting}
                  />
                  <TextInputComponent
                    label='Font size'
                    name='product_price_size'
                    value={pin.product_price_size}
                    onChange={updatePinSetting}
                  />
                  <SelectComponent
                    label='Font style'
                    options={[
                      { label: 'Normal', value: 'normal' },
                      { label: 'Italic', value: 'italic' },
                      { label: 'Oblique', value: 'oblique' },
                    ]}
                    name='product_price_font_style'
                    value={pin.product_price_font_style}
                    onChange={updatePinSetting}
                  />
                  <SelectComponent
                    label='Font weight'
                    options={[
                      { label: '100', value: '100' },
                      { label: '200', value: '200' },
                      { label: '300', value: '300' },
                      { label: '400', value: '400' },
                      { label: '500', value: '500' },
                      { label: '600', value: '600' },
                      { label: '700', value: '700' },
                      { label: '800', value: '800' },
                      { label: '900', value: '900' },
                    ]}
                    name='product_price_font_weight'
                    value={pin.product_price_font_weight}
                    onChange={updatePinSetting}
                  />
                </FormLayout>
              </AccordionItem>}

              {pin.display_data !== 'html' && parseInt(pin.preset_id) === 0 && <AccordionItem
                label='Quickview left button'>
                <FormLayout>
                  <SelectComponent
                    label='Button target'
                    options={[
                      { label: 'New window', value: 'new' },
                      { label: 'Same window', value: 'same' }
                    ]}
                    name='product_btn_one_open'
                    value={pin.product_btn_one_open}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Text color'
                    name='product_btn_one_color'
                    value={pin.product_btn_one_color}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Hover text color'
                    name='product_btn_one_hover_color'
                    value={pin.product_btn_one_hover_color}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Background color'
                    name='product_btn_one_bg'
                    value={pin.product_btn_one_bg}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Hover background color'
                    name='product_btn_one_hover_bg'
                    value={pin.product_btn_one_hover_bg}
                    onChange={updatePinSetting}
                  />
                  <TextInputComponent
                    label='Font size'
                    name='product_btn_one_size'
                    value={pin.product_btn_one_size}
                    onChange={updatePinSetting}
                  />
                  <SelectComponent
                    label='Font style'
                    options={[
                      { label: 'Normal', value: 'normal' },
                      { label: 'Italic', value: 'italic' },
                      { label: 'Oblique', value: 'oblique' },
                    ]}
                    name='product_btn_one_font_style'
                    value={pin.product_btn_one_font_style}
                    onChange={updatePinSetting}
                  />
                  <SelectComponent
                    label='Font weight'
                    options={[
                      { label: '100', value: '100' },
                      { label: '200', value: '200' },
                      { label: '300', value: '300' },
                      { label: '400', value: '400' },
                      { label: '500', value: '500' },
                      { label: '600', value: '600' },
                      { label: '700', value: '700' },
                      { label: '800', value: '800' },
                      { label: '900', value: '900' },
                    ]}
                    name='product_btn_one_font_weight'
                    value={pin.product_btn_one_font_weight}
                    onChange={updatePinSetting}
                  />
                </FormLayout>
              </AccordionItem>}

              {pin.display_data !== 'html' && parseInt(pin.preset_id) === 0 && <AccordionItem
                label='Quickview right button'>
                <FormLayout>
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Text color'
                    name='product_btn_two_color'
                    value={pin.product_btn_two_color}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Hover text color'
                    name='product_btn_two_hover_color'
                    value={pin.product_btn_two_hover_color}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Background color'
                    name='product_btn_two_bg'
                    value={pin.product_btn_two_bg}
                    onChange={updatePinSetting}
                  />
                  <ColorPickerComponent
                    buttonFullWidth={true}
                    label='Hover background color'
                    name='product_btn_two_hover_bg'
                    value={pin.product_btn_two_hover_bg}
                    onChange={updatePinSetting}
                  />
                  <TextInputComponent
                    label='Font size'
                    name='product_btn_two_size'
                    value={pin.product_btn_two_size}
                    onChange={updatePinSetting}
                  />
                  <SelectComponent
                    label='Font style'
                    options={[
                      { label: 'Normal', value: 'normal' },
                      { label: 'Italic', value: 'italic' },
                      { label: 'Oblique', value: 'oblique' },
                    ]}
                    name='product_btn_two_font_style'
                    value={pin.product_btn_two_font_style}
                    onChange={updatePinSetting}
                  />
                  <SelectComponent
                    label='Font weight'
                    options={[
                      { label: '100', value: '100' },
                      { label: '200', value: '200' },
                      { label: '300', value: '300' },
                      { label: '400', value: '400' },
                      { label: '500', value: '500' },
                      { label: '600', value: '600' },
                      { label: '700', value: '700' },
                      { label: '800', value: '800' },
                      { label: '900', value: '900' },
                    ]}
                    name='product_btn_two_font_weight'
                    value={pin.product_btn_two_font_weight}
                    onChange={updatePinSetting}
                  />
                </FormLayout>
              </AccordionItem>}
            </div>
          </div>
          <div
            style={{
              alignItems: 'center',
              borderTop: '1px solid #DFE3E8',
              display: 'flex',
              justifyContent: 'space-between',
              padding: '1.6rem',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex'
              }}
            >
              {
                props.showDeleteAction &&
                <Button destructive onClick={toggleDeleteModalChange}>Delete</Button>
              }
              {
                props.showDuplicateAction &&
                (
                  <div>
                    <span style={{ marginLeft: '1.6rem' }}/>
                    <Button onClick={duplicatePin}>Duplicate</Button>
                  </div>
                )
              }
            </div>
            {
              props.showSaveAction &&
              <Button primary onClick={savePinAction}>Save</Button>
            }
          </div>
        </div>
      </Sheet>}
      <MapperPreviewComponent show={activePinIndex !== null} presetData={props.presetData}/>
    </div>
  )
}

export default MapperPinsOptionsComponent
